import type {
  SuccessfulHttpStatusCode,
  AppRoute,
  AppRouteMutation,
  AppRouteQuery,
  ClientInferResponses,
  HTTPStatusCode,
  InferHeadersInput,
  FetchOptions,
  OverrideableClientArgs,
  ClientArgs,
  ApiFetcher,
  ClientInferRequest,
} from '@acre-rewards/lib'
import { z } from 'zod/v4'
import type { EndpointBuilder } from '@reduxjs/toolkit/query/react'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BaseBuilder = EndpointBuilder<any, string, string>

type NonSuccessStatus = Exclude<HTTPStatusCode, SuccessfulHttpStatusCode>

type IsNullable<T> = undefined extends T ? true : null extends T ? true : false

type IsMutation<T> = T extends { method: AppRouteMutation['method'] } ? true : false

type NullableObject = object | undefined | null

type AllFieldsAreNullable<T extends NullableObject> = {
  [Key in keyof T]-?: IsNullable<T[Key]>
}[keyof T] extends true
  ? true
  : false

type OptionalizeArgIfAllFieldsAreNullable<
  Obj extends object,
  Key extends keyof Obj = keyof Obj,
> = Obj extends {
  [key in Key]: infer Schema extends z.ZodType
}
  ? z.input<Schema> extends infer Arg extends NullableObject
    ? AllFieldsAreNullable<Arg> extends true
      ? { [key in Key]-?: z.ZodNullable<z.ZodOptional<z.ZodType<unknown, Arg>>> }
      : { [key in Key]-?: z.ZodType<unknown, z.input<Schema>> }
    : unknown
  : unknown

/** @see {@link https://github.com/ts-rest/ts-rest/blob/dd7239bc950d1e254d9c9cd6df7dbccc529c33cb/libs/ts-rest/core/src/lib/infer-types.ts#L266-L268} */
type RequiredKeys<T> = { [K in keyof T]-?: T[K] }

/** @see {@link https://github.com/ts-rest/ts-rest/blob/dd7239bc950d1e254d9c9cd6df7dbccc529c33cb/libs/ts-rest/core/src/lib/infer-types.ts#L35-L39} */
type ExtractExtraParametersFromClientArgs<TClientArgs extends Pick<ClientArgs, 'api'>> =
  TClientArgs['api'] extends ApiFetcher
    ? Omit<Parameters<TClientArgs['api']>[0], keyof Parameters<ApiFetcher>[0]>
    : // eslint-disable-next-line @typescript-eslint/no-empty-object-type
      {}

/** @see {@link https://github.com/ts-rest/ts-rest/blob/dd7239bc950d1e254d9c9cd6df7dbccc529c33cb/libs/ts-rest/core/src/lib/infer-types.ts#L242-L261} */
type TsRestClientRequestExtraArgs<Route extends AppRoute> = {
  extraHeaders?: [Route['headers']] extends [never]
    ? Record<string, string>
    : {
        [K in keyof RequiredKeys<InferHeadersInput<Route>>]?: never
      } & Record<string, string>
  fetchOptions?: FetchOptions
  overrideClientOptions?: Partial<OverrideableClientArgs>
} & ExtractExtraParametersFromClientArgs<Omit<ClientArgs, 'baseUrl'>>

/** `ClientInferRequest` causes `Type instantiation is excessively deep and possibly infinite` issues and doesn't optionalize inputs that should be optional (that is excluding them, rather than including them as an empty object, should be allowed) */
export type EndpointArgs<Route extends AppRoute> = z.input<
  z.ZodObject<
    {} & (Route['headers'] extends infer H extends {
      [key: string]: z.ZodType<string | undefined>
    }
      ? {
          headers: z.ZodOptional<
            z.ZodIntersection<z.ZodRecord<z.ZodString, z.ZodOptional<z.ZodString>>, z.ZodObject<H>>
          >
        }
      : unknown) &
      OptionalizeArgIfAllFieldsAreNullable<Route, 'query'> &
      (Route extends AppRouteMutation
        ? OptionalizeArgIfAllFieldsAreNullable<Route, 'body'>
        : unknown) &
      (Route extends { pathParams: infer Params } ? { params: Params } : unknown)
  >
> &
  TsRestClientRequestExtraArgs<Route> extends infer Args extends object
  ? AllFieldsAreNullable<Args> extends true
    ? Args | null | undefined
    : Args
  : never

export type SuccessResult<Route extends AppRoute> = Extract<
  ClientInferResponses<Route>,
  { status: SuccessfulHttpStatusCode }
>

// eslint-disable-next-line @typescript-eslint/no-unused-vars
type NonSuccessResult<Route extends AppRoute> = Extract<
  ClientInferResponses<Route>,
  { status: NonSuccessStatus }
>

type FilterByMethod<T, Method extends string> = T extends `${Method} ${string}` ? T : never

export function generateEndpointFromContract<TContract extends Record<string, AppRoute>>({
  builder,
  contract,
  endpoint,
}: {
  builder: BaseBuilder
  contract: TContract
  endpoint: string & keyof TContract
}) {
  const route = contract[endpoint]
  if (!route) throw new Error(`Route ${endpoint} not found in contract`)
  const isQuery = route.method === 'GET'

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  // const argSchema =
  //   route['query'] || route['body'] || route['pathParams']
  //     ? {
  //         // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  //         argSchema: z.object({
  //           ...(route['query'] ? { query: route['query'] } : {}),
  //           ...(route['body'] ? { body: route['body'] } : {}),
  //           ...(route['pathParams'] ? { params: route['pathParams'] } : {}),
  //           // eslint-disable-next-line @typescript-eslint/no-explicit-any
  //         }) as any,
  //       }
  //     : {}

  const configuredRTKQEndpoint = isQuery
    ? builder.query<
        SuccessResult<TContract[FilterByMethod<TContract, AppRouteQuery['method']>]>['body'],
        EndpointArgs<TContract[FilterByMethod<TContract, AppRouteQuery['method']>]>
      >({
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        query: (args) => ({ route, args: args as unknown as ClientInferRequest<typeof route> }),
        // ...argSchema,
        providesTags: (result, error) =>
          result
            ? Array.isArray(result)
              ? [endpoint] // ? [...result.map(({ id }) => ({ type: key, id }))] King: To sync with BE re standard to utilize
              : [endpoint]
            : error && typeof error === 'object' && 'status' in error && error.status === 401
              ? (['UNAUTHORIZED'] as const)
              : [endpoint],
      })
    : builder.mutation<
        SuccessResult<TContract[FilterByMethod<TContract, AppRouteMutation['method']>]>['body'],
        EndpointArgs<TContract[FilterByMethod<TContract, AppRouteMutation['method']>]>
      >({
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        query: (args) => ({ route, args: args as unknown as ClientInferRequest<typeof route> }),
        // ...argSchema,
      })

  return configuredRTKQEndpoint
}

export function generateEndpointsFromContract<TContract extends Record<string, AppRoute>>({
  builder,
  contract,
}: {
  builder: BaseBuilder
  contract: TContract
}) {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return Object.fromEntries(
    z
      .object(contract)
      .keyof()
      .options.map((endpoint) => [
        endpoint,
        generateEndpointFromContract({ builder, contract, endpoint }),
      ]),
  ) as unknown as {
    [Endpoint in keyof TContract]: IsMutation<TContract[Endpoint]> extends true
      ? ReturnType<
          typeof builder.mutation<
            SuccessResult<TContract[Endpoint]>['body'],
            EndpointArgs<TContract[Endpoint]>
          >
        >
      : ReturnType<
          typeof builder.query<
            SuccessResult<TContract[Endpoint]>['body'],
            EndpointArgs<TContract[Endpoint]>
          >
        >
  }
}
